import React from "react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import * as XLSX from "xlsx";
import "survey-core/survey-core.min.css";
import { themeJson } from "./theme";
import "./index.css";
import { json } from "./json";

function stripHtml(html) {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function flattenSurveyData(data, parentKey = "") {
    return Object.entries(data).flatMap(([key, value]) => {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;

        if (Array.isArray(value)) {
            return [{ question: fullKey, answer: JSON.stringify(value) }];
        }

        if (value && typeof value === "object") {
            return flattenSurveyData(value, fullKey);
        }

        return [{
            question: fullKey,
            answer: value ?? "",
        }];
    });
}

function createExportFileName() {
    const timestamp = new Date().toISOString().replace(/[.:]/g, "-");
    return `survey-results-${timestamp}.xlsx`;
}

function getChoiceText(options = [], value) {
    const match = options.find((option) => String(option.value) === String(value));
    return match ? match.text : value ?? "";
}

function getQuestionAnswerRows(element, data) {
    if (element.type === "html") {
        const note = stripHtml(element.html || "");
        return note ? [["Megjegyzés", note]] : [];
    }

    if (element.type === "panel") {
        const panelRows = [];

        if (element.title) {
            panelRows.push([element.title, ""]);
        }

        (element.elements || []).forEach((childElement) => {
            panelRows.push(...getQuestionAnswerRows(childElement, data));
        });

        return panelRows;
    }

    if (element.type === "matrix") {
        const matrixValue = data[element.name] || {};
        const matrixRows = [];

        if (element.title) {
            matrixRows.push([element.title, ""]);
        }

        if (element.description) {
            matrixRows.push(["Leírás", element.description]);
        }

        (element.rows || []).forEach((row) => {
            const answerValue = matrixValue[row.value];
            matrixRows.push([
                row.text,
                getChoiceText(element.columns, answerValue),
            ]);
        });

        return matrixRows;
    }

    const answerValue = data[element.name];

    if (element.type === "radiogroup") {
        return [[
            element.title || element.name,
            getChoiceText(element.choices, answerValue),
        ]];
    }

    if (element.type === "text" || element.type === "comment") {
        return [[element.title || element.name, answerValue ?? ""]];
    }

    return [[element.title || element.name, answerValue ?? ""]];
}

function buildReadableSheet(schema, data) {
    const rows = [[schema.title || "Kérdőív export", ""]];
    const merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];

    if (schema.description) {
        rows.push([schema.description, ""]);
        merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 1 } });
    }

    rows.push(["", ""]);

    (schema.pages || []).forEach((page) => {
        const pageHeaderRowIndex = rows.length;
        rows.push([page.title || page.name, ""]);
        merges.push({ s: { r: pageHeaderRowIndex, c: 0 }, e: { r: pageHeaderRowIndex, c: 1 } });
        rows.push(["Kérdés / állítás", "Válasz"]);

        (page.elements || []).forEach((element) => {
            rows.push(...getQuestionAnswerRows(element, data));
        });

        rows.push(["", ""]);
    });

    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet["!merges"] = merges;
    sheet["!cols"] = [
        { wch: 80 },
        { wch: 45 },
    ];
    sheet["!autofilter"] = { ref: "A4:B4" };

    return sheet;
}

function exportSurveyToExcel(survey) {
    const workbook = XLSX.utils.book_new();
    const flattenedAnswers = flattenSurveyData(survey.data);
    const answersSheet = XLSX.utils.json_to_sheet(flattenedAnswers);
    const rawSheet = XLSX.utils.json_to_sheet([
        {
            exportedAt: new Date().toISOString(),
            data: JSON.stringify(survey.data, null, 2),
        },
    ]);
    const readableSheet = buildReadableSheet(json, survey.data);

    XLSX.utils.book_append_sheet(workbook, readableSheet, "Kitoltott kerdoiv");
    XLSX.utils.book_append_sheet(workbook, answersSheet, "Answers");
    XLSX.utils.book_append_sheet(workbook, rawSheet, "RawData");
    XLSX.writeFile(workbook, createExportFileName());
}

function SurveyComponent() {
    const survey = new Model(json);
    survey.applyTheme(themeJson);
    survey.onComplete.add((sender, options) => {
        console.log(JSON.stringify(sender.data, null, 3));
        exportSurveyToExcel(sender);
    });
    return (<Survey model={survey} />);
}

export default SurveyComponent;
